import { IconAdd } from '@consta/icons/IconAdd'
import { IconClose } from '@consta/icons/IconClose'
import { IconDataNull } from '@consta/icons/IconDataNull'
import { IconTrash } from '@consta/icons/IconTrash'
import { Button } from '@consta/uikit/Button'
import { Checkbox } from '@consta/uikit/Checkbox'
import { Select } from '@consta/uikit/Select'
import { Text } from '@consta/uikit/Text'
import { withTooltip } from '@consta/uikit/withTooltip'
import { Placeholder } from '@core/components'
import { getSimpleTooltipContent } from '@core/core/helpers'
import type { Nullable } from 'common/types'
import type { ChangeEventHandler, FC } from 'react'
import { useEffect, useState } from 'react'

import type { MultipartUploadFileReturnType } from '../types'
import css from './FileImport.css'
import { FilesCard } from './FilesCard'
import type { UploadedFileOriginItemType } from './types'
import { originItems } from './types'

const ButtonDeleteAllWithTooltip = withTooltip({
  direction: 'downCenter',
  content: getSimpleTooltipContent('Удалить выбранные файлы'),
  size: 's',
})(Button)

const lineHeight = { lineHeight: '2.4rem' }

type Props = {
  onImportSidebarClose: () => void
  uploadedFiles: MultipartUploadFileReturnType[]
  removeOpenFiles: (fileIds: string[], tasksIds?: string[]) => void
  onMasterImportOpen: (fileId: string) => void
  addFilesToProject: (fileIds: string[]) => void
}

export const FileImport: FC<Props> = ({
  onImportSidebarClose,
  uploadedFiles,
  removeOpenFiles,
  onMasterImportOpen,
  addFilesToProject,
}) => {
  const [selectedCards, setSelectedCards] = useState<string[]>([])
  const [selectedOrigin, setSelectedOrigin] = useState<UploadedFileOriginItemType>()
  const [allCardSelected, setAllCardSelected] = useState(false)

  useEffect(() => {
    setAllCardSelected(selectedCards.length === uploadedFiles.length)
  }, [uploadedFiles, selectedCards])

  const isFiles = !!uploadedFiles.length
  const isFileReady = (id: string) =>
    uploadedFiles.find(file => file.fileId === id)?.uploadedFile?.statuses?.ready === 'ready'
  const canAddFiles = selectedCards.some(isFileReady)

  const onSelectCard = (fileId: string, checked: boolean) => {
    setSelectedCards(prev => (checked ? [...prev, fileId] : prev.filter(card => card !== fileId)))
  }
  const onAddFilesToProject = () => {
    addFilesToProject(selectedCards.filter(isFileReady))
  }

  const onChangeChecked: ChangeEventHandler<HTMLInputElement> = event => {
    setAllCardSelected(!allCardSelected)
    if (event.target.checked) {
      uploadedFiles.forEach(i => {
        setSelectedCards(prev => {
          if (i.fileId && !selectedCards.includes(i.fileId)) {
            return [...prev, i.fileId]
          }
          return prev
        })
      })
    } else setSelectedCards([])
  }
  const onRemoveButtonClick = () => {
    uploadedFiles
      .filter(file => file.fileId && selectedCards.includes(file.fileId))
      .forEach(file => {
        if (file.fileId) {
          onRemoveButtonOnCardClick(file.fileId, file.uploadedFile?.convertTaskId)
        }
      })
  }
  const onOriginChange = (value: Nullable<UploadedFileOriginItemType>) => {
    if (value) {
      setSelectedOrigin(value)
    }
  }
  const onRemoveButtonOnCardClick = (fieldId: string, taskId?: string) => {
    if (taskId) {
      removeOpenFiles([fieldId], [taskId])
    } else {
      removeOpenFiles([fieldId])
    }

    setSelectedCards(prev => prev.filter(card => card !== fieldId))
  }

  return (
    <div className={css.container}>
      <div className={css.wrapper}>
        <Text size="xs" weight="medium" style={lineHeight} view="secondary">
          Загрузка файлов
        </Text>
        <Button
          size="xs"
          iconSize="xs"
          iconRight={IconClose}
          onlyIcon={true}
          view="clear"
          onClick={onImportSidebarClose}
        />
      </div>

      <div className={css.controlsForFilesCard}>
        <Select
          size="xs"
          items={originItems}
          value={selectedOrigin}
          className={css.originSelect}
          onChange={onOriginChange}
        />
      </div>

      <div className={css.body}>
        {isFiles ? (
          uploadedFiles.map(file => (
            <div key={file.fileId} className={css.filesCardWrapper}>
              <FilesCard
                file={file}
                removeCard={onRemoveButtonOnCardClick}
                selected={selectedCards.some(card => card === file.fileId)}
                onMasterImportOpen={onMasterImportOpen}
                onSelectCard={onSelectCard}
              />
            </div>
          ))
        ) : (
          <div className={css.placeholderWrapper}>
            <Placeholder
              icon={<IconDataNull size="m" view="ghost" />}
              description={
                <Text className={css.placeholderText} view="secondary" size="xs">
                  Перетащите файлы или нажмите на кнопку
                </Text>
              }
            />
          </div>
        )}
      </div>
      <div className={css.buttonsWrapper}>
        <Checkbox
          label="Все"
          size="s"
          checked={allCardSelected}
          disabled={!isFiles}
          onChange={onChangeChecked}
        />
        <div>
          <ButtonDeleteAllWithTooltip
            size="s"
            view="ghost"
            onlyIcon={true}
            iconRight={IconTrash}
            iconSize="xs"
            disabled={!selectedCards.length}
            onClick={onRemoveButtonClick}
          />
          <Button
            label="В проект"
            iconLeft={IconAdd}
            className={css.toProject}
            size="s"
            disabled={!canAddFiles}
            onClick={onAddFilesToProject}
          />
        </div>
      </div>
    </div>
  )
}
